/**
 * User Pass Service
 * 
 * API service layer for fetching user pass details by ID.
 * Used for the subscription confirmation page to fetch purchased pass details.
 * 
 * @module subscription/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import type { ApiResult } from '~/lib/apiClient/types';
// Canonical payload (date-honest ISO strings; `pass`/`club` optional relations).
import type { GetUserPassByIdPayload } from '@lgc/database/types/domains/pass';

/**
 * User Pass Service
 * 
 * Handles fetching user pass details by ID
 */
export const userPassService = {
  /**
   * Get user pass by ID
   * 
   * Fetches a specific user pass with pass and club details.
   * Used for confirmation page after purchase.
   * 
   * Endpoint: GET /api/public/v1/userPasses/:id
   * 
   * @param id - User pass ID
   * @returns ApiResult with payload containing { userPass: UserPassResponse }
   * 
   * @example
   * ```typescript
   * const result = await userPassService.getUserPassById(123);
   * 
   * if (result.success) {
   *   console.log('Pass:', result.payload.userPass.pass.name);
   *   console.log('Club:', result.payload.userPass.club.name);
   *   console.log('Expires:', result.payload.userPass.expirationDate);
   * }
   * ```
   */
  async getUserPassById(id: number): Promise<ApiResult<GetUserPassByIdPayload>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    return await apiClient.get<GetUserPassByIdPayload>(
      `/userPasses/${id}`,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },
};
